import {
  DEFAULT_TITLE_FONT_ID,
  quoteFamily,
  resolveTitleFontSelection,
} from './fontLibrary'
import {
  TEXT_OUTLINE_WIDTH_RENDER_PX_MAX,
  TEXT_SHADOW_BLUR_RENDER_PX_MAX,
  TEXT_SHADOW_OFFSET_RENDER_PX_MAX,
  normalizeTextOutlineWidthControl,
  normalizeTextShadowBlurControl,
  normalizeTextShadowOffsetControl,
  textOutlineWidthControlToPx,
  textShadowBlurControlToPx,
  textShadowOffsetControlToPx,
} from './textEffectControls'

const DEFAULT_TEXT_RENDER_STATE = {
  text: '',
  'font.family': DEFAULT_TITLE_FONT_ID,
  'font.size': 96,
  'font.weight': 700,
  'font.style': 'normal',
  'font.color': '#FFFFFF',
  'font.lineHeight': 1.05,
  'font.letterSpacing': 0,
  'font.align': 'center',
  'effect.outlineWidth': 0,
  'effect.outlineColor': '#000000',
  'effect.shadowColor': '#000000',
  'effect.shadowBlur': 0,
  'effect.shadowOffsetX': 0,
  'effect.shadowOffsetY': 0,
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const finiteNumber = (value, fallback) => {
  const next = Number(value)
  return Number.isFinite(next) ? next : fallback
}

function normalizeText(value) {
  return `${value == null ? '' : value}`.replace(/\r\n/g, '\n')
}

export function normalizeTextRenderState(next = {}) {
  const text = normalizeText(next.text ?? DEFAULT_TEXT_RENDER_STATE.text)
  const align = ['left', 'center', 'right'].includes(next['font.align'])
    ? next['font.align']
    : DEFAULT_TEXT_RENDER_STATE['font.align']

  return {
    text,
    'font.family': next['font.family'] || DEFAULT_TEXT_RENDER_STATE['font.family'],
    'font.size': clamp(finiteNumber(next['font.size'], DEFAULT_TEXT_RENDER_STATE['font.size']), 8, 512),
    'font.weight': clamp(finiteNumber(next['font.weight'], DEFAULT_TEXT_RENDER_STATE['font.weight']), 100, 900),
    'font.style': next['font.style'] === 'italic' ? 'italic' : 'normal',
    'font.color': next['font.color'] || DEFAULT_TEXT_RENDER_STATE['font.color'],
    'font.lineHeight': clamp(finiteNumber(next['font.lineHeight'], DEFAULT_TEXT_RENDER_STATE['font.lineHeight']), 0.7, 3),
    'font.letterSpacing': clamp(finiteNumber(next['font.letterSpacing'], DEFAULT_TEXT_RENDER_STATE['font.letterSpacing']), -20, 120),
    'font.align': align,
    'effect.outlineWidth': normalizeTextOutlineWidthControl(next['effect.outlineWidth'], DEFAULT_TEXT_RENDER_STATE['effect.outlineWidth']),
    'effect.outlineColor': next['effect.outlineColor'] || DEFAULT_TEXT_RENDER_STATE['effect.outlineColor'],
    'effect.shadowColor': next['effect.shadowColor'] || DEFAULT_TEXT_RENDER_STATE['effect.shadowColor'],
    'effect.shadowBlur': normalizeTextShadowBlurControl(next['effect.shadowBlur'], DEFAULT_TEXT_RENDER_STATE['effect.shadowBlur']),
    'effect.shadowOffsetX': normalizeTextShadowOffsetControl(next['effect.shadowOffsetX'], DEFAULT_TEXT_RENDER_STATE['effect.shadowOffsetX']),
    'effect.shadowOffsetY': normalizeTextShadowOffsetControl(next['effect.shadowOffsetY'], DEFAULT_TEXT_RENDER_STATE['effect.shadowOffsetY']),
  }
}

function fitTextWithinBox(state, {
  boxWidth,
  boxHeight,
  widthRatio = 0.72,
  heightRatio = 0.32,
  minFontSize = 24,
  maxFontSize = null,
} = {}) {
  const bw = finiteNumber(boxWidth, 0)
  const bh = finiteNumber(boxHeight, 0)
  if (!(bw > 0) || !(bh > 0)) return state
  if (typeof document === 'undefined') return state

  const targetWidth = bw * clamp(finiteNumber(widthRatio, 0.72), 0.1, 1)
  const targetHeight = bh * clamp(finiteNumber(heightRatio, 0.32), 0.1, 1)
  const minSize = clamp(Math.round(finiteNumber(minFontSize, 24)), 8, 512)
  const maxSize = clamp(
    Math.round(
      finiteNumber(
        maxFontSize,
        state['font.size']
      )
    ),
    minSize,
    512
  )
  const scratch = document.createElement('canvas')

  const fits = (fontSize) => {
    const result = renderTextToCanvas(scratch, {
      ...state,
      'font.size': fontSize,
    })
    return result.width <= targetWidth && result.height <= targetHeight
  }

  if (!fits(minSize)) {
    return {
      ...state,
      'font.size': minSize,
    }
  }

  let low = minSize
  let high = maxSize
  let best = Math.min(maxSize, Math.max(minSize, Math.round(state['font.size'])))

  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    if (fits(mid)) {
      best = mid
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  return {
    ...state,
    'font.size': best,
  }
}

export function fitTextRenderStateToBox(input = {}, options = {}) {
  const state = normalizeTextRenderState(input)
  const text = `${state.text || ''}`.trim()
  if (!text) return state
  return fitTextWithinBox(state, options)
}

function buildFontString(selection, fontSize) {
  return `${selection.style} ${selection.weight} ${fontSize}px ${quoteFamily(selection.family)}, ${selection.fallback || `'Helvetica Neue', Helvetica, Arial, sans-serif`}`
}

function splitLines(text) {
  const lines = normalizeText(text).split('\n')
  return lines.length ? lines : ['']
}

const graphemeSegmenter = typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
  ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
  : null

function splitGraphemes(line) {
  if (!graphemeSegmenter) return Array.from(line)
  return Array.from(graphemeSegmenter.segment(line), (part) => part.segment)
}

function supportsNativeLetterSpacing(ctx) {
  return typeof ctx.letterSpacing === 'string'
}

function applyLetterSpacing(ctx, letterSpacing) {
  if (supportsNativeLetterSpacing(ctx)) ctx.letterSpacing = `${letterSpacing}px`
}

function measureLineWidth(ctx, line, letterSpacing) {
  if (!line) return 0
  if (Math.abs(letterSpacing) < 1e-6) return ctx.measureText(line).width
  // Native spacing is also added after the last glyph; exclude it from layout.
  if (supportsNativeLetterSpacing(ctx)) return ctx.measureText(line).width - letterSpacing

  const glyphs = splitGraphemes(line)
  return glyphs.reduce((width, glyph, index) => {
    const nextWidth = width + ctx.measureText(glyph).width
    if (index >= glyphs.length - 1) return nextWidth
    return nextWidth + letterSpacing
  }, 0)
}

function drawLine(ctx, line, x, y, align, letterSpacing, outlineWidth) {
  if (!line) return
  if (Math.abs(letterSpacing) < 1e-6 || supportsNativeLetterSpacing(ctx)) {
    let drawX = x
    if (align === 'center') drawX += letterSpacing / 2
    if (align === 'right') drawX += letterSpacing
    ctx.textAlign = align
    if (outlineWidth > 0) ctx.strokeText(line, drawX, y)
    ctx.fillText(line, drawX, y)
    return
  }

  const lineWidth = measureLineWidth(ctx, line, letterSpacing)
  let cursorX = x
  if (align === 'center') cursorX -= lineWidth / 2
  if (align === 'right') cursorX -= lineWidth

  splitGraphemes(line).forEach((glyph) => {
    if (outlineWidth > 0) ctx.strokeText(glyph, cursorX, y)
    ctx.fillText(glyph, cursorX, y)
    cursorX += ctx.measureText(glyph).width + letterSpacing
  })
}

export function renderTextToCanvas(canvas, input = {}) {
  const state = normalizeTextRenderState(input)
  const selection = resolveTitleFontSelection(
    state['font.family'],
    state['font.weight'],
    state['font.style']
  )
  const text = state.text || ' '
  const lines = splitLines(text)
  const fontSize = state['font.size']
  const lineHeight = state['font.lineHeight']
  const letterSpacing = state['font.letterSpacing']
  const outlineWidth = textOutlineWidthControlToPx(state['effect.outlineWidth'])
  const shadowBlur = textShadowBlurControlToPx(state['effect.shadowBlur'])
  const shadowOffsetX = textShadowOffsetControlToPx(state['effect.shadowOffsetX'])
  const shadowOffsetY = textShadowOffsetControlToPx(state['effect.shadowOffsetY'])
  const align = state['font.align']
  const padding = Math.max(
    24,
    TEXT_OUTLINE_WIDTH_RENDER_PX_MAX * 4,
    TEXT_SHADOW_BLUR_RENDER_PX_MAX * 2 + TEXT_SHADOW_OFFSET_RENDER_PX_MAX + 12
  )

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return {
      state,
      selection,
      width: 1,
      height: 1,
    }
  }

  ctx.font = buildFontString(selection, fontSize)
  applyLetterSpacing(ctx, letterSpacing)
  const metrics = ctx.measureText('Mg')
  const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.8
  const descent = metrics.actualBoundingBoxDescent || fontSize * 0.22
  const lineHeightPx = Math.max(ascent + descent, fontSize * lineHeight)
  const maxLineWidth = Math.max(...lines.map((line) => measureLineWidth(ctx, line, letterSpacing)), 1)
  const layoutWidth = Math.ceil(maxLineWidth + padding * 2)
  const layoutHeight = Math.ceil(padding * 2 + ascent + descent + Math.max(0, lines.length - 1) * lineHeightPx)
  const deviceScale = typeof window !== 'undefined'
    ? clamp(window.devicePixelRatio || 1, 1, 2)
    : 1

  canvas.width = Math.max(1, Math.ceil(layoutWidth * deviceScale))
  canvas.height = Math.max(1, Math.ceil(layoutHeight * deviceScale))

  ctx.setTransform(deviceScale, 0, 0, deviceScale, 0, 0)
  ctx.clearRect(0, 0, layoutWidth, layoutHeight)
  ctx.font = buildFontString(selection, fontSize)
  applyLetterSpacing(ctx, letterSpacing)
  ctx.textBaseline = 'alphabetic'
  ctx.lineJoin = 'round'
  ctx.fillStyle = state['font.color']
  ctx.strokeStyle = state['effect.outlineColor']
  ctx.lineWidth = outlineWidth
  ctx.shadowColor = state['effect.shadowColor']
  ctx.shadowBlur = shadowBlur
  ctx.shadowOffsetX = shadowOffsetX
  ctx.shadowOffsetY = shadowOffsetY

  const anchorX = align === 'left'
    ? padding
    : align === 'right'
      ? layoutWidth - padding
      : layoutWidth / 2

  lines.forEach((line, index) => {
    const y = padding + ascent + index * lineHeightPx
    drawLine(ctx, line, anchorX, y, align, letterSpacing, outlineWidth)
  })

  return {
    state,
    selection,
    width: layoutWidth,
    height: layoutHeight,
  }
}

export function renderTextThumbnailToCanvas(canvas, input = {}, {
  frameWidth = 0,
  frameHeight = 0,
  longEdge = 480,
  padding = 24,
} = {}) {
  const contentCanvas = document.createElement('canvas')
  const result = renderTextToCanvas(contentCanvas, input)
  const frameW = finiteNumber(frameWidth, 0)
  const frameH = finiteNumber(frameHeight, 0)
  const aspect = frameW > 0 && frameH > 0
    ? frameW / frameH
    : Math.max(1e-6, result.width / Math.max(result.height, 1))
  const edge = clamp(Math.round(finiteNumber(longEdge, 480)), 160, 1024)

  let layoutWidth = edge
  let layoutHeight = edge
  if (aspect >= 1) {
    layoutWidth = edge
    layoutHeight = Math.max(1, Math.round(edge / aspect))
  } else {
    layoutHeight = edge
    layoutWidth = Math.max(1, Math.round(edge * aspect))
  }

  canvas.width = layoutWidth
  canvas.height = layoutHeight

  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return {
      ...result,
      width: layoutWidth,
      height: layoutHeight,
    }
  }

  ctx.clearRect(0, 0, layoutWidth, layoutHeight)
  ctx.imageSmoothingEnabled = true

  const usableWidth = Math.max(1, layoutWidth - padding * 2)
  const usableHeight = Math.max(1, layoutHeight - padding * 2)
  const scale = Math.min(
    1,
    usableWidth / Math.max(result.width, 1),
    usableHeight / Math.max(result.height, 1)
  )
  const drawWidth = result.width * scale
  const drawHeight = result.height * scale
  const dx = padding
  const dy = (layoutHeight - drawHeight) / 2

  ctx.drawImage(contentCanvas, dx, dy, drawWidth, drawHeight)

  return {
    ...result,
    width: layoutWidth,
    height: layoutHeight,
    contentWidth: result.width,
    contentHeight: result.height,
    scale,
  }
}

